import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Badge,
  Alert,
  Form,
  Modal,
  Spinner,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaToggleOn,
  FaToggleOff,
  FaUserCog,
  FaSearch,
  FaUsers,
  FaUser
} from 'react-icons/fa';
import AssignmentRuleApi from '../../../api/AssignmentRuleApi';
import ObjectConfigApi from '../../../api/ObjectConfigApi';
import { getEntitlementsSection } from '../../../utils/entitlements';
import AssignmentRuleForm from './AssignmentRuleForm';
import './css/AssignmentRuleManager.css';

const AssignmentRuleManager = () => {
  const navigate = useNavigate();
  const assignmentRuleEnt = getEntitlementsSection('assignmentRule', {});
  const allowViewAssignmentRule = assignmentRuleEnt.allowViewAssignmentRule !== false;
  const allowCreateAssignmentRule = assignmentRuleEnt.allowCreateAssignmentRule !== false;
  const allowedObjectsList = Array.isArray(assignmentRuleEnt.allowedObjects) ? assignmentRuleEnt.allowedObjects : null;
  const allowedObjectsSet = useMemo(
    () => (allowedObjectsList === null ? null : new Set(allowedObjectsList)),
    [allowedObjectsList]
  );

  const [rules, setRules] = useState([]);
  const [objects, setObjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('all'); // all, active, inactive
  const [filterObject, setFilterObject] = useState('all'); // all or specific object
  const [filterAssignType, setFilterAssignType] = useState('all'); // all, user, group
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState(null);

  const visibleObjects = useMemo(() => {
    if (allowedObjectsSet === null) return objects;
    return objects.filter((o) => allowedObjectsSet.has(o.name));
  }, [objects, allowedObjectsSet]);

  const rulesForAllowedObjects = useMemo(() => {
    if (allowedObjectsSet === null) return rules;
    return rules.filter((r) => allowedObjectsSet.has(r.object_name));
  }, [rules, allowedObjectsSet]);

  useEffect(() => {
    if (!allowViewAssignmentRule) {
      navigate('/setup', { replace: true });
      return;
    }
    fetchObjects();
    fetchAllRules();
  }, [allowViewAssignmentRule, navigate]);

  const fetchObjects = async () => {
    try {
      const response = await ObjectConfigApi.fetchObjectConfigs();
      if (response?.success) {
        const objectList = response.data
          .filter(obj => !obj.is_deleted)
          .map(obj => ({
            name: obj.object_name,
            label: obj.object_label || obj.object_name,
            id: obj.id
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
        setObjects(objectList);
      }
    } catch (err) {
      console.error('Error fetching objects:', err);
      setError('Failed to fetch objects');
    }
  };

  const fetchAllRules = async () => {
    setLoading(true);
    setError('');
    try {
      console.log('Fetching all assignment rules...');
      const assignmentRuleApi = new AssignmentRuleApi();
      const response = await assignmentRuleApi.fetchAll(null, false); // Fetch all rules including inactive
      console.log('Fetch all rules - Full response:', response);

      if (response?.data?.success) {
        const rulesData = response.data.data || [];
        console.log(`Successfully fetched ${rulesData.length} rules`);
        setRules(rulesData);
      } else {
        const errorMsg = response?.data?.message || 'Failed to fetch assignment rules';
        console.error('Fetch failed - Error message:', errorMsg);
        setError(errorMsg);
      }
    } catch (err) {
      console.error('Error fetching rules (catch block):', err);
      let errorMessage = 'Failed to fetch assignment rules';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = `Error: ${err.message}`;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRule = () => {
    setEditingRule(null);
    setError('');
    setShowModal(true);
  };

  const handleEditRule = (rule) => {
    console.log('🔍 Editing rule - full data:', rule);
    console.log('🔍 assign_to_details:', rule.assign_to_details);
    setEditingRule(rule);
    setShowModal(true);
  };

  const handleSaveRule = async (ruleData, setModalError) => {
    try {
      console.log('Saving rule data:', ruleData);
      const assignmentRuleApi = new AssignmentRuleApi();
      let response;

      if (editingRule) {
        // Update existing rule
        response = await assignmentRuleApi.updateRule(editingRule.id, ruleData);
      } else {
        // Create new rule
        response = await assignmentRuleApi.createRule(ruleData);
      }

      console.log('Save response:', response);

      if (response?.data?.success) {
        setSuccess(editingRule ? 'Rule updated successfully!' : 'Rule created successfully!');
        setShowModal(false);
        fetchAllRules();
        setTimeout(() => setSuccess(''), 3000);
        return { success: true };
      } else {
        let errorMessage = 'Failed to save rule';
        if (response?.data?.message) {
          errorMessage = response.data.message;
        } else if (response?.data?.error) {
          errorMessage = response.data.error;
        } else if (response?.data?.errors && Array.isArray(response.data.errors)) {
          errorMessage = response.data.errors.map(e => e.msg || e.message || e).join(', ');
        }

        if (setModalError) {
          setModalError(errorMessage);
        }
        return { success: false, message: errorMessage };
      }
    } catch (err) {
      console.error('Error saving rule (catch block):', err);
      let errorMessage = 'Failed to save rule';

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.data?.errors && Array.isArray(err.response.data.errors)) {
        errorMessage = err.response.data.errors.map(e => e.msg || e.message || e).join(', ');
      } else if (err.message) {
        errorMessage = err.message;
      }

      if (setModalError) {
        setModalError(errorMessage);
      }
      return { success: false, message: errorMessage };
    }
  };

  const handleToggleRule = async (rule) => {
    try {
      const assignmentRuleApi = new AssignmentRuleApi();
      const response = await assignmentRuleApi.toggleStatus(rule.id);
      if (response?.data?.success) {
        setSuccess(`Rule ${rule.is_active ? 'deactivated' : 'activated'} successfully!`);
        fetchAllRules();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response?.data?.message || 'Failed to toggle rule');
      }
    } catch (err) {
      console.error('Error toggling rule:', err);
      setError('Failed to toggle rule');
    }
  };

  const handleDeleteClick = (rule) => {
    setRuleToDelete(rule);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!ruleToDelete) return;

    try {
      const assignmentRuleApi = new AssignmentRuleApi();
      const response = await assignmentRuleApi.deleteRule(ruleToDelete.id);
      if (response?.data?.success) {
        setSuccess('Rule deleted successfully!');
        setShowDeleteConfirm(false);
        setRuleToDelete(null);
        fetchAllRules();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response?.data?.message || 'Failed to delete rule');
      }
    } catch (err) {
      console.error('Error deleting rule:', err);
      setError('Failed to delete rule');
    }
  };

  const getObjectLabel = (objectName) => {
    const obj = objects.find((o) => o.name === objectName);
    return obj?.label || objectName;
  };

  if (!allowViewAssignmentRule) {
    return null;
  }

  // Filter and search rules (only rules for allowed objects, then apply filters)
  const filteredRules = rulesForAllowedObjects.filter((rule) => {
    // Filter by object
    if (filterObject !== 'all' && rule.object_name !== filterObject) return false;

    // Filter by active status
    if (filterActive === 'active' && !rule.is_active) return false;
    if (filterActive === 'inactive' && rule.is_active) return false;

    // Filter by assign type
    if (filterAssignType !== 'all' && rule.assign_type !== filterAssignType) return false;

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        rule.name?.toLowerCase().includes(search) ||
        rule.description?.toLowerCase().includes(search) ||
        rule.object_name?.toLowerCase().includes(search) ||
        rule.assign_to_details?.name?.toLowerCase().includes(search) ||
        rule.assign_to_details?.firstname?.toLowerCase().includes(search) ||
        rule.assign_to_details?.lastname?.toLowerCase().includes(search)
      );
    }

    return true;
  });

  const renderConditionsSummary = (rule) => {
    if (!rule.conditions) return 'No conditions';

    let conditions = rule.conditions;
    if (typeof conditions === 'string') {
      try {
        conditions = JSON.parse(conditions);
      } catch (e) {
        return 'Invalid conditions';
      }
    }

    if (!Array.isArray(conditions)) return 'No conditions';
    if (conditions.length === 0) return 'Always assign';

    return `${conditions.length} condition${conditions.length !== 1 ? 's' : ''}`;
  };

  const renderAssignmentTarget = (rule) => {
    if (!rule.assign_to_details) {
      return <span className="text-muted">Not set</span>;
    }

    const details = rule.assign_to_details;

    if (rule.assign_type === 'user') {
      return (
        <div className="d-flex align-items-center">
          <FaUser className="me-2 text-primary" />
          <span>{`${details.firstname || ''} ${details.lastname || ''}`.trim() || details.email || 'Unknown User'}</span>
        </div>
      );
    } else if (rule.assign_type === 'group') {
      return (
        <div className="d-flex align-items-center">
          <FaUsers className="me-2 text-info" />
          <span>{details.name || 'Unknown Group'}</span>
        </div>
      );
    }

    return <span className="text-muted">Unknown</span>;
  };

  const getStrategyBadgeVariant = (strategy) => {
    switch (strategy) {
      case 'round_robin': return 'primary';
      case 'load_based': return 'success';
      case 'least_active': return 'warning';
      case 'manual': return 'secondary';
      default: return 'secondary';
    }
  };

  const getStrategyLabel = (strategy) => {
    switch (strategy) {
      case 'round_robin': return 'Round Robin';
      case 'load_based': return 'Load Based';
      case 'least_active': return 'Least Active';
      case 'manual': return 'Manual';
      default: return strategy;
    }
  };

  return (
    <Container fluid className="assignment-rule-manager">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2>
                <FaUserCog className="me-2 text-primary" />
                Assignment Rule Manager
              </h2>
              <p className="text-muted">
                Create and manage assignment rules to automatically assign records to users or groups
              </p>
            </div>
            {allowCreateAssignmentRule && (
              <Button
                variant="primary"
                onClick={handleCreateRule}
              >
                <FaPlus className="me-2" />
                New Assignment Rule
              </Button>
            )}
          </div>
        </Col>
      </Row>

      {/* Alerts */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Filters */}
      <Row className="mb-4">
        <Col md={3}>
          <Form.Group>
            <Form.Label>Filter by Object</Form.Label>
            <Form.Select
              value={filterObject}
              onChange={(e) => setFilterObject(e.target.value)}
            >
              <option value="all">All Objects</option>
              {visibleObjects.map((obj) => (
                <option key={obj.name} value={obj.name}>
                  {obj.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={3}>
          <Form.Group>
            <Form.Label>Filter by Status</Form.Label>
            <Form.Select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
            >
              <option value="all">All Rules</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={3}>
          <Form.Group>
            <Form.Label>Filter by Assign Type</Form.Label>
            <Form.Select
              value={filterAssignType}
              onChange={(e) => setFilterAssignType(e.target.value)}
            >
              <option value="all">All Types</option>
              <option value="user">User</option>
              <option value="group">Group</option>
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={3}>
          <Form.Group>
            <Form.Label>Search Rules</Form.Label>
            <Form.Control
              type="text"
              placeholder="Search by name, object, assignee..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Form.Group>
        </Col>
      </Row>

      {/* Rules Table */}
      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="mt-2">Loading assignment rules...</p>
            </div>
          ) : filteredRules.length === 0 ? (
            <div className="text-center py-5">
              <FaUserCog size={48} className="text-muted mb-3" />
              <h5>No Assignment Rules Found</h5>
              <p className="text-muted">
                {rulesForAllowedObjects.length === 0
                  ? (visibleObjects.length === 0
                    ? 'No objects are allowed for assignment rules in your plan.'
                    : 'Create your first assignment rule to get started')
                  : 'No rules match your current filters'}
              </p>
              {rulesForAllowedObjects.length === 0 && allowCreateAssignmentRule && visibleObjects.length > 0 && (
                <Button variant="primary" onClick={handleCreateRule} className="mt-3">
                  <FaPlus className="me-2" />
                  Create First Rule
                </Button>
              )}
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Object</th>
                  <th>Rule Name</th>
                  <th>Status</th>
                  <th>Assign To</th>
                  <th>Strategy</th>
                  <th>Priority</th>
                  <th>Conditions</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRules.map(rule => (
                  <tr key={rule.id}>
                    <td>
                      <Badge bg="info">{getObjectLabel(rule.object_name)}</Badge>
                    </td>
                    <td>
                      <div className="fw-bold">{rule.name || 'Untitled Rule'}</div>
                      {rule.description && (
                        <small className="text-muted">{rule.description}</small>
                      )}
                    </td>
                    <td>
                      <Badge bg={rule.is_active ? 'success' : 'secondary'}>
                        {rule.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td>
                      {renderAssignmentTarget(rule)}
                      <div>
                        <Badge bg={rule.assign_type === 'user' ? 'primary' : 'info'} className="mt-1">
                          {rule.assign_type}
                        </Badge>
                      </div>
                    </td>
                    <td>
                      <Badge bg={getStrategyBadgeVariant(rule.assignment_strategy)}>
                        {getStrategyLabel(rule.assignment_strategy)}
                      </Badge>
                    </td>
                    <td>
                      <Badge bg="dark">{rule.priority || 0}</Badge>
                    </td>
                    <td>
                      <small className="text-muted">
                        {renderConditionsSummary(rule)}
                      </small>
                    </td>
                    <td>
                      <small className="text-muted">
                        {new Date(rule.createddate).toLocaleDateString()}
                      </small>
                    </td>
                    <td>
                      {allowCreateAssignmentRule ? (
                        <div className="d-flex gap-2">
                          <OverlayTrigger overlay={<Tooltip>Edit Rule</Tooltip>}>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleEditRule(rule)}
                            >
                              <FaEdit />
                            </Button>
                          </OverlayTrigger>
                          <OverlayTrigger overlay={<Tooltip>{rule.is_active ? 'Deactivate' : 'Activate'}</Tooltip>}>
                            <Button
                              variant={rule.is_active ? 'outline-warning' : 'outline-success'}
                              size="sm"
                              onClick={() => handleToggleRule(rule)}
                            >
                              {rule.is_active ? <FaToggleOn /> : <FaToggleOff />}
                            </Button>
                          </OverlayTrigger>
                          <OverlayTrigger overlay={<Tooltip>Delete Rule</Tooltip>}>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteClick(rule)}
                            >
                              <FaTrash />
                            </Button>
                          </OverlayTrigger>
                        </div>
                      ) : (
                        <span className="text-muted small">View only</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Create/Edit Modal */}
      {showModal && (
        <AssignmentRuleForm
          show={showModal}
          onHide={() => setShowModal(false)}
          onSave={handleSaveRule}
          editingRule={editingRule}
          objects={visibleObjects}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the rule <strong>"{ruleToDelete?.name}"</strong>?
          <br />
          This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AssignmentRuleManager;
